import OnboardingPageTitle from "./OnboardingPageTitle";
import { FormSelect } from "../../components/ui/select";
import i18n from "../../i18n";import { Trans } from "react-i18next";import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./EmergencyInfo.css";
import { resolveApiUrl } from "../../api/api";
import sessionDB from "../../utils/sessionDB";
import {
  isOnboardingFeatureUnavailableError,
  isGenericOnboardingErrorMessage,
  parseOnboardingErrorResponse,
  handleOnboardingApiError
} from "./onboardingFeatureAccess";

const ACTION_ORDER = ["add", "update", "search"];

const ACTIONS = {
  add: {
    title: "Add Emergency Info",
    serviceCode: "HR06A",
    endpoint: "/saas/hrms/emergency-info/add",
    method: "POST"
  },
  update: {
    title: "Update Emergency Info",
    serviceCode: "HR06U",
    endpoint: "/saas/hrms/emergency-info/update/{empId}",
    method: "PUT"
  },
  search: {
    title: "Search Emergency Info List",
    serviceCode: "HR06S",
    endpoint: "/saas/hrms/emergency-info/search",
    method: "POST"
  }
};

const MARITAL_STATUSES = ["Single", "Married", "Divorced", "Widowed", "Separated"];
const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

const MARITAL_STATUS_OPTION_KEYS = {
  Single: "onboarding.options.maritalStatus.single",
  Married: "onboarding.options.maritalStatus.married",
  Divorced: "onboarding.options.maritalStatus.divorced",
  Widowed: "onboarding.options.maritalStatus.widowed",
  Separated: "onboarding.options.maritalStatus.separated"
};

const BLOOD_GROUP_OPTION_KEYS = {
  "A+": "onboarding.options.bloodGroup.aPositive",
  "A-": "onboarding.options.bloodGroup.aNegative",
  "B+": "onboarding.options.bloodGroup.bPositive",
  "B-": "onboarding.options.bloodGroup.bNegative",
  "AB+": "onboarding.options.bloodGroup.abPositive",
  "AB-": "onboarding.options.bloodGroup.abNegative",
  "O+": "onboarding.options.bloodGroup.oPositive",
  "O-": "onboarding.options.bloodGroup.oNegative"
};

const FIELD_LABEL_KEYS = {
  empId: "common.employeeId",
  maritalStatus: "onboarding.labels.maritalStatus",
  bloodGroup: "onboarding.labels.bloodGroup",
  emergencyContactName: "onboarding.labels.emergencyContactName",
  emergencyContact: "onboarding.labels.emergencyContactNumber",
  fatherName: "onboarding.labels.fatherName",
  motherName: "onboarding.labels.motherName",
  medicationInfo: "onboarding.labels.medicationInfo",
  noOfChildren: "onboarding.labels.numberOfChildren"
};

function getFieldLabel(name) {
  const key = FIELD_LABEL_KEYS[name];
  return key ? i18n.t(key) : name;
}

function getFieldLabelsMap() {
  return Object.fromEntries(
    Object.keys(FIELD_LABEL_KEYS).map((name) => [name, getFieldLabel(name)])
  );
}

function getRuleMessage(errorKey, fieldName) {
  const fullKey = `onboarding.errors.${errorKey}`;
  return i18n.exists(fullKey)
    ? i18n.t(fullKey)
    : i18n.t("validation.fieldRequired", { field: getFieldLabel(fieldName) });
}

function translateMaritalStatusLabel(value) {
  const key = MARITAL_STATUS_OPTION_KEYS[value];
  return key ? i18n.t(key) : value;
}

function translateBloodGroupLabel(value) {
  const key = BLOOD_GROUP_OPTION_KEYS[value];
  return key ? i18n.t(key) : value;
}

const INITIAL_FORM = {
  empId: "",
  maritalStatus: "",
  bloodGroup: "",
  emergencyContactName: "",
  emergencyContact: "",
  fatherName: "",
  motherName: "",
  medicationInfo: "",
  noOfChildren: "0"
};

const REQUIRED_BY_ACTION = {
  add: ["empId", "maritalStatus", "bloodGroup", "emergencyContactName", "emergencyContact", "fatherName", "motherName"],
  update: ["empId"],
  search: []
};

const RULES = {
  empId: (value) => !value.trim() ? getRuleMessage("empIdRequired", "empId") : !/^\d+$/.test(value.trim()) ? getRuleMessage("empIdMustBeNumeric", "empId") : "",
  maritalStatus: (value) => !value ? "" : !MARITAL_STATUSES.includes(value) ? getRuleMessage("chooseValidMaritalStatus", "maritalStatus") : "",
  bloodGroup: (value) => !value ? "" : !BLOOD_GROUPS.includes(value) ? getRuleMessage("chooseValidBloodGroup", "bloodGroup") : "",
  emergencyContactName: (value) => !value ? "" : value.trim().length < 2 ? i18n.t("onboarding.errors.enterFullContactName") : value.length > 100 ? i18n.t("onboarding.errors.max100Characters") : "",
  emergencyContact: (value) => !value ? "" : !/^\+?\d[\d\s-]{8,19}$/.test(value.trim()) ? i18n.t("onboarding.errors.useValidContactNumber") : "",
  fatherName: (value) => !value ? "" : value.trim().length < 2 ? i18n.t("onboarding.errors.enterFatherFullName") : value.length > 100 ? i18n.t("onboarding.errors.max100Characters") : "",
  motherName: (value) => !value ? "" : value.trim().length < 2 ? i18n.t("onboarding.errors.enterMotherFullName") : value.length > 100 ? i18n.t("onboarding.errors.max100Characters") : "",
  medicationInfo: (value) => !value ? "" : value.length > 500 ? i18n.t("onboarding.errors.max500Characters") : "",
  noOfChildren: (value) => !value ? "" : !/^\d+$/.test(value.trim()) ? i18n.t("onboarding.errors.enterValidNumber") : Number(value) > 20 ? i18n.t("onboarding.errors.maxChildren20") : ""
};

function buildUrl(template, form) {
  return template.replace("{empId}", encodeURIComponent(form.empId.trim()));
}

function getNextAction(currentAction) {
  const currentIndex = ACTION_ORDER.indexOf(currentAction);
  return ACTION_ORDER[(currentIndex + 1) % ACTION_ORDER.length];
}

function Field({ id, label, required, error, hint, children, span2 = false }) {
  return (
    <div className={`einfo-field${span2 ? " span2" : ""}`}>
      <label className={`einfo-label${required ? " required" : ""}`} htmlFor={id}>{label}</label>
      {children}
      {hint ? <p className="einfo-hint">{hint}</p> : null}
      <p className="einfo-error" role="alert">{error || ""}</p>
    </div>);

}

export default function EmergencyInfo({ accessToken, tenantCode, onBack, onNext, onSuccess, breadcrumb, initialEmployeeId = "", isTabMode = false, isActive = true, saveTriggerRef }) {
  useEffect(() => {
    if (saveTriggerRef) {
      saveTriggerRef.current = {
        submit: handleSave
      };
    }
  });
  const [action, setAction] = useState("add");
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [banner, _setBanner] = useState(null);
  const setBanner = (val) => {
    _setBanner((prev) => {
      const next = typeof val === "function" ? val(prev) : val;
      if (next && next.type === "error") {
        if (isOnboardingFeatureUnavailableError(next.msg)) {
          return { type: "error", msg: i18n.t("onboarding.featureUnavailable") };
        }
        if (isGenericOnboardingErrorMessage(next.msg)) {
          return prev;
        }
      }
      return next;
    });
  };
  const requiredFields = useMemo(() => new Set(REQUIRED_BY_ACTION[action] || []), [action]);
  const currentAction = ACTIONS[action];
  const tenantId = sessionDB.getTenantId();
  const sessionAccessToken = accessToken || sessionDB.getAccessToken();

  const fetchEmergency = useCallback(async () => {
    if (!initialEmployeeId) return;
    try {
      const response = await fetch(resolveApiUrl(`/saas/hrms/emergency-info/search/${initialEmployeeId}`), {
        method: 'GET',
        headers: {
          'x-tenant-code': tenantCode || sessionDB.getTenantCode() || "",
          'x-tenant-id': String(tenantId || sessionDB.getTenantId() || ""),
          'x-service-code': 'HR06S',
          authorization: `Bearer ${sessionAccessToken || sessionDB.getAccessToken() || ""}`
        }
      });
      if (response.ok) {
        const body = await response.json();
        const data = body.data || body;
        const candidates = [data, data?.data, data?.content, data?.records, data?.items];
        const arrayCandidate = candidates.find((c) => Array.isArray(c));
        const record = arrayCandidate ? arrayCandidate[0] : data && typeof data === "object" ? data : null;

        if (record) {
          setForm({
            empId: String(initialEmployeeId),
            maritalStatus: record.maritalStatus || "",
            bloodGroup: record.bloodGroup || "",
            emergencyContactName: record.emergencyContactName || "",
            emergencyContact: record.emergencyContact || "",
            fatherName: record.fatherName || "",
            motherName: record.motherName || "",
            medicationInfo: record.medicationInfo || "",
            noOfChildren: String(record.noOfChildren || "0")
          });
          setAction("update");
        }
      }
    } catch (err) {
      console.error("Failed to load emergency info", err);
    }
  }, [initialEmployeeId, tenantCode, tenantId, sessionAccessToken]);

  useEffect(() => {
    if (!isActive) return;
    if (!initialEmployeeId) {
      setForm({
        empId: "",
        maritalStatus: "",
        bloodGroup: "",
        emergencyContactName: "",
        emergencyContact: "",
        fatherName: "",
        motherName: "",
        medicationInfo: "",
        noOfChildren: ""
      });
      setAction("add");
      return;
    }
    setForm((prev) => ({ ...prev, empId: String(initialEmployeeId) }));
    fetchEmergency();
  }, [isActive, initialEmployeeId, fetchEmergency]);

  function resetFeedback() {
    setBanner(null);
  }

  function validateField(name, value, touchMap = touched) {
    const requiredMessage = requiredFields.has(name) && !String(value || "").trim() ? i18n.t("validation.fieldRequired", { field: getFieldLabel(name) }) : "";
    const ruleMessage = RULES[name] ? RULES[name](value || "") : "";
    const message = requiredMessage || ruleMessage;
    setErrors((prev) => ({ ...prev, [name]: touchMap[name] ? message : "" }));
    return !message;
  }

  function validateAll() {
    const nextTouched = {};
    const nextErrors = {};
    let valid = true;

    Object.keys(RULES).forEach((name) => {
      nextTouched[name] = true;
      const requiredMessage = requiredFields.has(name) && !String(form[name] || "").trim() ? i18n.t("validation.fieldRequired", { field: getFieldLabel(name) }) : "";
      const ruleMessage = RULES[name] ? RULES[name](form[name] || "") : "";
      const message = requiredMessage || ruleMessage;
      nextErrors[name] = message;
      if (message) {
        valid = false;
      }
    });

    setTouched(nextTouched);
    setErrors(nextErrors);
    return valid;
  }

  function handleActionChange(nextAction) {
    setAction(nextAction);
    setErrors({});
    setTouched({});
    resetFeedback();
  }

  function handleInputChange(event) {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      validateField(name, value);
    }
  }

  function handleInputBlur(event) {
    const { name, value } = event.target;
    const nextTouched = { ...touched, [name]: true };
    setTouched(nextTouched);
    validateField(name, value, nextTouched);
  }

  async function submitMutation() {
    if (!validateAll()) {
      return;
    }

    setLoading(true);
    resetFeedback();

    try {
      const actionConfig = ACTIONS[action] || ACTIONS.add;
      const payload = {
        empId: Number(form.empId.trim()),
        maritalStatus: form.maritalStatus || undefined,
        emergencyContactName: form.emergencyContactName.trim() || undefined,
        emergencyContact: form.emergencyContact.trim() || undefined,
        fatherName: form.fatherName.trim() || undefined,
        motherName: form.motherName.trim() || undefined,
        healthReport: "NA",
        medicationInfo: form.medicationInfo.trim() || undefined,
        bloodGroup: form.bloodGroup || undefined,
        noOfChildren: form.noOfChildren !== "" ? Number(form.noOfChildren) : undefined
      };

      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined || payload[key] === "") {
          delete payload[key];
        }
      });

      async function sendMutationRequest(nextActionConfig) {
        return fetch(resolveApiUrl(buildUrl(nextActionConfig.endpoint, form)), {
          method: nextActionConfig.method,
          headers: {
            "Content-Type": "application/json",
            "x-tenant-code": tenantCode || "",
            "x-tenant-id": String(tenantId || ""),
            "x-service-code": nextActionConfig.serviceCode,
            authorization: `Bearer ${sessionAccessToken}`
          },
          body: JSON.stringify(payload)
        });
      }

      const response = await sendMutationRequest(actionConfig);

      if (!response.ok) {
        const { errorMessage, errorBody } = await parseOnboardingErrorResponse(response, getFieldLabelsMap());
        handleOnboardingApiError(errorBody, errorMessage, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
        return;
      }

      const data = await response.json().catch(() => ({
        message: action === "add"
          ? i18n.t("onboarding.messages.addEmergencyInfoCompleted")
          : i18n.t("onboarding.messages.updateEmergencyInfoCompleted")
      }));
      setBanner({
        type: "success",
        msg: data.message || (action === "add"
          ? i18n.t("onboarding.messages.addEmergencyInfoCompleted")
          : i18n.t("onboarding.messages.updateEmergencyInfoCompleted"))
      });
      if (action === "update") {
        await fetchEmergency();
      }
      if (onSuccess) {
        onSuccess(data);
      }
    } catch (error) {
      handleOnboardingApiError(null, error.message, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
    } finally {
      setLoading(false);
    }
  }

  async function runSearch() {
    setLoading(true);
    resetFeedback();

    try {
      const response = await fetch(resolveApiUrl(currentAction.endpoint), {
        method: currentAction.method,
        headers: {
          "Content-Type": "application/json",
          "x-tenant-code": tenantCode || "",
          "x-tenant-id": String(tenantId || ""),
          "x-service-code": currentAction.serviceCode,
          authorization: `Bearer ${sessionAccessToken}`
        },
        body: JSON.stringify({
          empId: form.empId.trim() ? Number(form.empId.trim()) : undefined,
          maritalStatus: form.maritalStatus || undefined,
          bloodGroup: form.bloodGroup || undefined,
          emergencyContactName: form.emergencyContactName.trim() || undefined,
          fatherName: form.fatherName.trim() || undefined,
          motherName: form.motherName.trim() || undefined
        })
      });

      if (!response.ok) {
        const { errorMessage, errorBody } = await parseOnboardingErrorResponse(response, getFieldLabelsMap());
        handleOnboardingApiError(errorBody, errorMessage, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
        return;
      }

      await response.json();
      setBanner({ type: "success", msg: i18n.t("onboarding.messages.emergencyInfoResultsLoaded") });
    } catch (error) {
      handleOnboardingApiError(null, error.message, { setErrors, setTouched, setBanner, fieldLabels: getFieldLabelsMap() });
    } finally {
      setLoading(false);
    }
  }

  function handleSave() {
    if (action === "search") {
      runSearch();
      return;
    }
    submitMutation();
  }

  function handleNext() {
    const nextAction = getNextAction(action);
    handleActionChange(nextAction);
    if (onNext) {
      onNext(nextAction);
    }
  }

  return (
    <div className={`einfo-page${isTabMode ? " is-tab-mode" : ""}`}>

      <main className="einfo-main">
        {!isTabMode && breadcrumb &&
        <div className="einfo-breadcrumb" aria-label={i18n.t("common.breadcrumb")}>
            {breadcrumb.map((item, index) =>
          <React.Fragment key={item.label}>
                {index > 0 && <span className="einfo-bc-sep">/</span>}
                <button
              className={`einfo-bc-item${item.active ? " active" : ""}`}
              onClick={item.active ? undefined : item.onClick}
              disabled={item.active}>

                  {item.label}
                </button>
              </React.Fragment>
          )}
          </div>
        }

        {!isTabMode &&
        <OnboardingPageTitle
          onBack={onBack}
          employeeId={initialEmployeeId}
          titleClassName="einfo-page-title"
          subtitleClassName="einfo-page-sub"
          title={<Trans i18nKey="onboarding.emergencyInformation" />}
          subtitle={<Trans i18nKey="onboarding.manageEmergencyContactsFamilyDetailsHealthReportAnd" />}
          action={
            <button className="einfo-header-action" type="button" onClick={() => handleActionChange(action === "update" ? "add" : "update")}>
              {action === "update" ? i18n.t("onboarding.addNew") : i18n.t("common.update")}
            </button>
          }
        />
        }

        <div className="einfo-form-card">
          <div className="einfo-section">
            <p className="einfo-section-title"><Trans i18nKey="onboarding.emergencyDetails" /></p>
            <div className="einfo-grid">

              <Field id="maritalStatus" label={getFieldLabel("maritalStatus")} required error={touched.maritalStatus && errors.maritalStatus}>
                <div className="einfo-select-wrap">
                  <FormSelect id="maritalStatus" name="maritalStatus" value={form.maritalStatus} onChange={handleInputChange} onBlur={handleInputBlur} className={`einfo-select${touched.maritalStatus && errors.maritalStatus ? " error" : ""}`}>
                    <option value=""><Trans i18nKey="common.selectStatus" /></option>
                    {MARITAL_STATUSES.map((option) =>
                    <option key={option} value={option}>{translateMaritalStatusLabel(option)}</option>
                    )}
                  </FormSelect>
                </div>
              </Field>

              <Field id="bloodGroup" label={getFieldLabel("bloodGroup")} required error={touched.bloodGroup && errors.bloodGroup}>
                <div className="einfo-select-wrap">
                  <FormSelect id="bloodGroup" name="bloodGroup" value={form.bloodGroup} onChange={handleInputChange} onBlur={handleInputBlur} className={`einfo-select${touched.bloodGroup && errors.bloodGroup ? " error" : ""}`}>
                    <option value=""><Trans i18nKey="onboarding.selectBloodGroup" /></option>
                    {BLOOD_GROUPS.map((option) =>
                    <option key={option} value={option}>{translateBloodGroupLabel(option)}</option>
                    )}
                  </FormSelect>
                </div>
              </Field>

              <Field id="emergencyContactName" label={getFieldLabel("emergencyContactName")} required error={touched.emergencyContactName && errors.emergencyContactName}>
                <input id="emergencyContactName" name="emergencyContactName" value={form.emergencyContactName} onChange={handleInputChange} onBlur={handleInputBlur} className={`einfo-input${touched.emergencyContactName && errors.emergencyContactName ? " error" : ""}`} placeholder={i18n.t("onboarding.fullName")} />
              </Field>

              <Field id="emergencyContact" label={getFieldLabel("emergencyContact")} required error={touched.emergencyContact && errors.emergencyContact}>
                <input id="emergencyContact" name="emergencyContact" value={form.emergencyContact} onChange={handleInputChange} onBlur={handleInputBlur} className={`einfo-input${touched.emergencyContact && errors.emergencyContact ? " error" : ""}`} placeholder={i18n.t("onboarding.emergencyContactPlaceholder")} />
              </Field>

              <Field id="fatherName" label={getFieldLabel("fatherName")} required={requiredFields.has("fatherName")} error={touched.fatherName && errors.fatherName}>
                <input id="fatherName" name="fatherName" value={form.fatherName} onChange={handleInputChange} onBlur={handleInputBlur} className={`einfo-input${touched.fatherName && errors.fatherName ? " error" : ""}`} placeholder={i18n.t("onboarding.fatherSFullName")} />
              </Field>

              <Field id="motherName" label={getFieldLabel("motherName")} required={requiredFields.has("motherName")} error={touched.motherName && errors.motherName}>
                <input id="motherName" name="motherName" value={form.motherName} onChange={handleInputChange} onBlur={handleInputBlur} className={`einfo-input${touched.motherName && errors.motherName ? " error" : ""}`} placeholder={i18n.t("onboarding.motherSFullName")} />
              </Field>

              <Field id="noOfChildren" label={getFieldLabel("noOfChildren")} required error={touched.noOfChildren && errors.noOfChildren}>
                <input id="noOfChildren" name="noOfChildren" value={form.noOfChildren} onChange={handleInputChange} onBlur={handleInputBlur} className={`einfo-input${touched.noOfChildren && errors.noOfChildren ? " error" : ""}`} placeholder="0" />
              </Field>

              <Field id="medicationInfo" label={getFieldLabel("medicationInfo")} required error={touched.medicationInfo && errors.medicationInfo} span2>
                <textarea id="medicationInfo" name="medicationInfo" value={form.medicationInfo} onChange={handleInputChange} onBlur={handleInputBlur} className={`einfo-textarea${touched.medicationInfo && errors.medicationInfo ? " error" : ""}`} placeholder={i18n.t("onboarding.listAnyOngoingMedicationsOrHealthConditions")} rows="4" />
              </Field>
            </div>
          </div>

          <div className="einfo-section">
            {banner &&
            <div className={`einfo-banner ${banner.type}`} role="alert">
                {banner.msg}
              </div>
            }
            {!isTabMode &&
            <div className="einfo-actions">
                <button className="einfo-primary-btn" type="button" onClick={handleSave} disabled={loading}>
                  {loading ? i18n.t("localConveyance.saving") : i18n.t("formActions.save")}
                </button>
                <button className="einfo-secondary-btn" type="button" onClick={handleNext}><Trans i18nKey="pagination.next2" /></button>
              </div>
            }
          </div>
        </div>

      </main>
    </div>);

}
